import React, { useState } from 'react';

interface CustomHeadCombo {
  id: string;
  headId: number;
  headBrand: string;
  headModel: string;
  config: string | number;
  rating: 9 | 10;
  isCustom: true;
}

interface CustomShaftCombo {
  id: string;
  shaftId: number;
  shaftBrand: string;
  shaftModel: string;
  flex: string | number;
  rating: 9 | 10;
  isCustom: true;
}

interface EquipmentOption {
  id: number;
  brand: string;
  model: string;
  [key: string]: any;
}

interface CustomComboAdderProps {
  type: 'head' | 'shaft';
  category: 'driver' | 'iron' | 'wedge';
  equipmentOptions: EquipmentOption[];
  configOptions?: (string | number)[]; // For heads: loft or set config options
  flexOptions?: string[]; // For shafts: flex options
  customCombos: CustomHeadCombo[] | CustomShaftCombo[];
  maxCombos: number;
  onAdd: (id: number, brand: string, model: string, configOrFlex: string | number, rating: 9 | 10) => void;
  onRemove: (id: string) => void;
}

const CustomComboAdder: React.FC<CustomComboAdderProps> = ({
  type,
  category,
  equipmentOptions,
  configOptions = [],
  flexOptions = [],
  customCombos,
  maxCombos,
  onAdd,
  onRemove
}) => {
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<number | null>(null);
  const [selectedConfig, setSelectedConfig] = useState<string | number | null>(null);
  const [selectedRating, setSelectedRating] = useState<9 | 10>(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Filter equipment based on search term
  const filteredEquipment = equipmentOptions.filter(eq =>
    `${eq.brand} ${eq.model}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group equipment by brand
  const groupedEquipment = filteredEquipment.reduce((acc, eq) => {
    if (!acc[eq.brand]) {
      acc[eq.brand] = [];
    }
    acc[eq.brand].push(eq);
    return acc;
  }, {} as Record<string, EquipmentOption[]>);

  const selectedEquipment = equipmentOptions.find(eq => eq.id === selectedEquipmentId);

  const canAdd = selectedEquipmentId && selectedConfig && customCombos.length < maxCombos;

  const handleAdd = () => {
    if (!canAdd || !selectedEquipment) return;

    if (type === 'head') {
      onAdd(
        selectedEquipmentId,
        selectedEquipment.brand,
        selectedEquipment.model,
        selectedConfig,
        selectedRating
      );
    } else {
      onAdd(
        selectedEquipmentId,
        selectedEquipment.brand,
        selectedEquipment.model,
        selectedConfig, // This is flex for shafts
        selectedRating
      );
    }

    // Reset form
    setSelectedEquipmentId(null);
    setSelectedConfig(null);
    setSearchTerm('');
    setSelectedRating(10);
  };

  const getComboLabel = (combo: CustomHeadCombo | CustomShaftCombo) => {
    if (type === 'head') {
      const headCombo = combo as CustomHeadCombo;
      return `${headCombo.headBrand} ${headCombo.headModel} (${headCombo.config})`;
    } else {
      const shaftCombo = combo as CustomShaftCombo;
      return `${shaftCombo.shaftBrand} ${shaftCombo.shaftModel} (${shaftCombo.flex})`;
    }
  };

  return (
    <div className="border-t-4 border-yellow-400 bg-yellow-50 p-4 rounded-lg mt-6">
      <h4 className="font-bold text-gray-800 mb-2 flex items-center">
        <span className="text-2xl mr-2">⭐</span>
        Know a Perfect Match Not Listed?
      </h4>
      <p className="text-sm text-gray-600 mb-4">
        Add up to {maxCombos} {type === 'head' ? `${category} head${category === 'driver' ? '+loft' : '+config'}` : 'shaft+flex'} combos you rate as 9 or 10
      </p>

      {customCombos.length < maxCombos && (
        <div className="bg-white border-2 border-yellow-300 rounded-lg p-4 mb-4">
          {/* Equipment Selection */}
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {type === 'head' ? `${category.charAt(0).toUpperCase() + category.slice(1)} Head` : 'Shaft'}:
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder={`🔍 Search ${type === 'head' ? 'heads' : 'shafts'}...`}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setIsDropdownOpen(true);
                }}
                onFocus={() => setIsDropdownOpen(true)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
              />

              {isDropdownOpen && filteredEquipment.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                  {Object.entries(groupedEquipment).map(([brand, items]) => (
                    <div key={brand}>
                      <div className="px-3 py-1 bg-gray-100 text-xs font-semibold text-gray-600 sticky top-0">
                        {brand}
                      </div>
                      {items.map(eq => (
                        <button
                          key={eq.id}
                          onClick={() => {
                            setSelectedEquipmentId(eq.id);
                            setSearchTerm(`${eq.brand} ${eq.model}`);
                            setIsDropdownOpen(false);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-yellow-50 text-sm border-b border-gray-100 last:border-0"
                        >
                          {eq.model}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Config/Flex Selection */}
          {selectedEquipmentId && (
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {type === 'head'
                  ? (category === 'driver' ? 'Loft' : (category === 'iron' ? 'Set Configuration' : 'Loft Configuration'))
                  : 'Flex'}:
              </label>
              <select
                value={selectedConfig || ''}
                onChange={(e) => setSelectedConfig(type === 'head' && category === 'driver' ? Number(e.target.value) : e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
              >
                <option value="">Select {type === 'head' ? 'configuration' : 'flex'}...</option>
                {(type === 'head' ? configOptions : flexOptions).map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          )}

          {/* Rating Selection */}
          {selectedEquipmentId && selectedConfig && (
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rating (9 or 10 only):
              </label>
              <div className="flex gap-3">
                <button
                  onClick={() => setSelectedRating(9)}
                  className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${
                    selectedRating === 9
                      ? 'bg-yellow-500 text-white shadow-md'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  9
                </button>
                <button
                  onClick={() => setSelectedRating(10)}
                  className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${
                    selectedRating === 10
                      ? 'bg-yellow-500 text-white shadow-md'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  10
                </button>
              </div>
            </div>
          )}

          {/* Add Button */}
          <button
            onClick={handleAdd}
            disabled={!canAdd}
            className={`w-full py-2 rounded-lg font-semibold transition-all ${
              canAdd
                ? 'bg-green-ink text-white hover:opacity-90 shadow-md'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            + Add This Perfect Match
          </button>
        </div>
      )}

      {/* Added Custom Combos */}
      {customCombos.length > 0 && (
        <div className="bg-white border-2 border-green-ink rounded-lg p-4">
          <h5 className="font-semibold text-gray-800 mb-2">Your Perfect Matches:</h5>
          <div className="space-y-2">
            {customCombos.map((combo, idx) => (
              <div
                key={combo.id}
                className="flex items-center justify-between p-2 bg-green/10 rounded border border-green-ink"
              >
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-800">
                    {idx + 1}. ✓ {getComboLabel(combo)}
                  </span>
                  <span className="ml-2 text-xs text-gray-600">- Rated: {combo.rating}</span>
                </div>
                <button
                  onClick={() => onRemove(combo.id)}
                  className="ml-2 px-3 py-1 text-xs font-medium text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition-colors"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
          <div className="mt-2 text-sm text-gray-600 font-medium">
            Added: {customCombos.length}/{maxCombos}
          </div>
        </div>
      )}

      {customCombos.length === 0 && (
        <div className="text-sm text-gray-500 italic">
          No custom {type === 'head' ? 'heads' : 'shafts'} added yet
        </div>
      )}
    </div>
  );
};

export default CustomComboAdder;
